import type {
  DiagnosticSeverity,
  DiagnosticTier,
  DiagnosticTriageStatus,
} from "admin/types/interfaces/diagnostics";

export const DIAGNOSTICS_API = "/api/conversations/v1/diagnostics";

// N.B. Badge colors below must stay within the design-system hues that
// `src/index.css` remaps and inverts under `.dark`: blue, gray, green, orange,
// purple, red, yellow. Those flip automatically in dark mode (`bg-red-100`
// becomes dark, `text-red-700` becomes light), so no `dark:` variants are
// needed. Raw Tailwind hues (amber, slate, indigo, zinc) have no dark mapping
// and would render as bright light-mode stickers on the nextgen dark theme.

/**
 * Severity badge configuration: colors and labels for each severity level.
 * Backend severity is `high | medium | low` (high = worst quality).
 */
export const SEVERITY_CONFIG = {
  high: {
    label: "High",
    badgeClass: "bg-red-100 text-red-700 border border-red-300",
  },
  medium: {
    label: "Medium",
    badgeClass: "bg-orange-100 text-orange-700 border border-orange-300",
  },
  low: {
    label: "Low",
    badgeClass: "bg-gray-100 text-gray-600 border border-gray-300",
  },
} as const satisfies Record<DiagnosticSeverity, { label: string; badgeClass: string }>;

/**
 * Tint for the in-session per-turn diagnostics indicator, which carries `unscored`
 * as a fourth state. Deliberately not `SEVERITY_CONFIG`: that map renders `low`
 * gray, which reads correctly in the dense triage table but is indistinguishable
 * from `unscored` when the icon is a turn's only diagnostics signal. Size and
 * shape are the indicator's own; only the tint varies by state.
 */
export const SEVERITY_INDICATOR_CONFIG = {
  high: {
    label: "High",
    iconButtonClass: "bg-red-100 text-red-600",
  },
  medium: {
    label: "Medium",
    iconButtonClass: "bg-orange-100 text-orange-600",
  },
  low: {
    label: "Low",
    iconButtonClass: "bg-green-100 text-green-600",
  },
  unscored: {
    label: "Not scored",
    iconButtonClass: "bg-muted text-muted-foreground",
  },
} as const satisfies Record<
  DiagnosticSeverity | "unscored",
  { label: string; iconButtonClass: string }
>;

/**
 * Tier badge configuration. `heuristics` turns are signals-only ($0);
 * `llm` turns carry full rubric scoring.
 */
export const TIER_CONFIG = {
  heuristics: {
    label: "Heuristics",
    badgeClass: "bg-gray-100 text-gray-600 border border-gray-300",
  },
  llm: {
    label: "LLM",
    badgeClass: "bg-purple-100 text-purple-700 border border-purple-300",
  },
} as const satisfies Record<DiagnosticTier, { label: string; badgeClass: string }>;

/**
 * Triage status options with display labels and Tailwind color classes.
 */
export const TRIAGE_STATUS_OPTIONS: {
  value: DiagnosticTriageStatus;
  label: string;
  badgeClass: string;
}[] = [
  {
    value: "unreviewed",
    label: "Unreviewed",
    badgeClass: "bg-gray-100 text-gray-700 border-gray-300",
  },
  {
    value: "reviewing",
    label: "Reviewing",
    badgeClass: "bg-blue-100 text-blue-700 border-blue-300",
  },
  {
    value: "ticketed",
    label: "Ticketed",
    badgeClass: "bg-purple-100 text-purple-700 border-purple-300",
  },
  {
    value: "wont_fix",
    label: "Won't Fix",
    badgeClass: "bg-red-100 text-red-700 border-red-300",
  },
  {
    value: "resolved",
    label: "Resolved",
    badgeClass: "bg-green-100 text-green-700 border-green-300",
  },
  {
    value: "no_action",
    label: "No Action",
    badgeClass: "bg-orange-100 text-orange-700 border-orange-300",
  },
  {
    value: "excluded",
    label: "Excluded",
    badgeClass: "bg-gray-100 text-gray-400 border-gray-300 italic",
  },
];

/**
 * Failure `applies_to` key to display name and Tailwind color mapping. These are the
 * rubric's `applies_to` values (v4.0) - a toolkit id or one of the two turn-level
 * buckets (`orchestration`, `cross_cutting`), naming an internal stage of the
 * analysis pipeline, not the user-facing Agents under `/nextgen/agents`.
 * "none" represents turns with no identified failure `applies_to` value. An id this map
 * does not recognize (a newer toolkit, or an unknown value) falls back to its
 * raw id via `getFailureAppliesToLabel` / a neutral style via
 * `getFailureAppliesToBadgeClass`, so the table never errors on it.
 */
export const FAILURE_APPLIES_TO_CONFIG = {
  orchestration: {
    label: "Orchestration",
    color: "#3b82f6",
    badgeClass: "bg-blue-100 text-blue-700",
  },
  data_retrieval: {
    label: "Data Retrieval",
    color: "#ef4444",
    badgeClass: "bg-red-100 text-red-700",
  },
  code_interpreter: {
    label: "Code Interpreter",
    color: "#f59e0b",
    badgeClass: "bg-orange-100 text-orange-700",
  },
  explore_dataset: {
    label: "Explore Dataset",
    color: "#10b981",
    badgeClass: "bg-green-100 text-green-700",
  },
  data_prep: {
    label: "Data Prep",
    color: "#eab308",
    badgeClass: "bg-yellow-100 text-yellow-700",
  },
  cross_cutting: {
    label: "Cross-Cutting",
    color: "#a855f7",
    badgeClass: "bg-purple-100 text-purple-700",
  },
  // Legacy v3.1 layer values, kept as display aliases so a historical
  // `primary_failure` stored before the v4.0 rekey onto toolkit `applies_to`
  // values stays readable in the triage table.
  orchestrator: {
    label: "Orchestrator",
    color: "#3b82f6",
    badgeClass: "bg-blue-100 text-blue-700",
  },
  data_retriever: {
    label: "Data Retrieval",
    color: "#ef4444",
    badgeClass: "bg-red-100 text-red-700",
  },
  explore_agent: {
    label: "Explore Dataset",
    color: "#10b981",
    badgeClass: "bg-green-100 text-green-700",
  },
  python_analyzer: {
    label: "Code Interpreter",
    color: "#f59e0b",
    badgeClass: "bg-orange-100 text-orange-700",
  },
  none: {
    label: "None",
    color: "#94a3b8",
    badgeClass: "bg-gray-100 text-gray-600",
  },
} as const;

export type FailureAppliesToKey = keyof typeof FAILURE_APPLIES_TO_CONFIG;

export function getFailureAppliesToLabel(layerKey: string | null): string {
  if (!layerKey) return "None";
  const key = layerKey.toLowerCase() as FailureAppliesToKey;
  return FAILURE_APPLIES_TO_CONFIG[key]?.label ?? layerKey;
}

export function getFailureAppliesToBadgeClass(layerKey: string | null): string {
  if (!layerKey) return FAILURE_APPLIES_TO_CONFIG.none.badgeClass;
  const key = layerKey.toLowerCase() as FailureAppliesToKey;
  return FAILURE_APPLIES_TO_CONFIG[key]?.badgeClass ?? "bg-gray-100 text-gray-600";
}

export function getTriageStatusConfig(
  status: DiagnosticTriageStatus
): (typeof TRIAGE_STATUS_OPTIONS)[number] {
  return TRIAGE_STATUS_OPTIONS.find((opt) => opt.value === status) ?? TRIAGE_STATUS_OPTIONS[0];
}

/**
 * Severity filter options for the triage table dropdown.
 * "all" is a UI-only sentinel that maps to an omitted filter.
 */
export const SEVERITY_FILTER_OPTIONS = [
  { value: "all", label: "All Severities" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
] as const;

/**
 * Failure `applies_to` filter options for the triage table dropdown (API key values,
 * sent as `primary_failure`). Not the session's Agent - see
 * `FAILURE_APPLIES_TO_CONFIG`. Current `applies_to` values only; the legacy v3.1 layer
 * values in `FAILURE_APPLIES_TO_CONFIG` are display aliases, not filterable here.
 * "all" is a UI-only sentinel that maps to an omitted filter.
 */
export const FAILURE_APPLIES_TO_FILTER_OPTIONS = [
  { value: "all", label: "All Areas" },
  { value: "orchestration", label: "Orchestration" },
  { value: "data_retrieval", label: "Data Retrieval" },
  { value: "code_interpreter", label: "Code Interpreter" },
  { value: "explore_dataset", label: "Explore Dataset" },
  { value: "data_prep", label: "Data Prep" },
  { value: "cross_cutting", label: "Cross-Cutting" },
] as const;

/**
 * Tier filter options for the triage table dropdown.
 * "all" is a UI-only sentinel that maps to an omitted filter.
 */
export const TIER_FILTER_OPTIONS = [
  { value: "all", label: "All Tiers" },
  { value: "llm", label: "LLM" },
  { value: "heuristics", label: "Heuristics" },
] as const;

export const TRIAGE_SORT_KEYS = {
  CREATED_AT: "created_at",
  SEVERITY: "severity",
  CONCERN_SCORE: "concern_score",
} as const;

export type TriageSortKey = (typeof TRIAGE_SORT_KEYS)[keyof typeof TRIAGE_SORT_KEYS];
